// utils/useLoadingStream.ts
import { useCallback, useState } from "react";

export type LoadingEventType =
  | "step"
  | "thinking"
  | "reflecting"
  | "code_generation_start"
  | "code_execution_start"
  | "generated_plots_processing"
  | "intermediate_steps";

export type LoadingItem = {
  type: LoadingEventType;
  content: string;
};

/**
 * Separates "thinking" from the rest.
 * - thinking: persists until clear() or end-of-turn
 * - step (and other non-thinking signals): replaces the steps container content
 */
export function useLoadingStream() {
  const [thinking, setThinking] = useState<LoadingItem | null>(null);
  const [steps, setSteps] = useState<LoadingItem | null>(null);

  const ingest = useCallback((type: LoadingEventType, content: string) => {
    const safe = content || "";

    if (type === "thinking") {
      // Append streaming 'thinking' packets for continuity.
      setThinking((prev) =>
        prev
          ? { type: "thinking", content: prev.content + safe }
          : { type, content: safe }
      );
      return;
    }

    if (type === "step") {
      // 'step' messages are usually short incremental updates.
      // Replace instead of append to avoid duplicate wording.
      setSteps({ type: "step", content: safe });
      return;
    }

    if (type === "intermediate_steps") {
      // 'intermediate_steps' arrives as a complete reasoning block.
      // Do NOT append; just store the full content.
      setSteps({ type: "intermediate_steps", content: safe });
      return;
    }

    // Fallback for other transient states (reflecting, code_execution, etc.)
    setSteps({ type, content: safe });
  }, []);

  const clear = useCallback(() => {
    setThinking(null);
    setSteps(null);
  }, []);

  return {
    thinking, // single persistent thinking item
    steps, // single current steps item
    ingest, // call this for every incoming loading event
    clear, // call at end of turn / error / close
  };
}
